import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { SectionField } from "../home/HomeTabs";
import UploadImage from "../../components/UploadImage";

const MAX_SECTIONS = 4;

interface ContentItem {
  label: string;
  subtext: string;
}

export interface whatWeStandForProps {
  heading?: string;
  subtext?: string;
  image?: string;
  content?: ContentItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: SectionField) => void;
  form: any;
  whatWeStandFor?: whatWeStandForProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const WhatWeStandFor: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whatWeStandFor,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  const sectionCount = whatWeStandFor?.content?.length ?? 0;
  const isLimitReached = sectionCount >= MAX_SECTIONS;

  useEffect(() => {
    if (!whatWeStandFor) return;

    if (mode === "list") {
      form.setFieldsValue({
        whatWeStandFor: {
          heading: whatWeStandFor.heading || "",
          subtext: whatWeStandFor.subtext || "",
          image: whatWeStandFor.image || "",
          content:
            whatWeStandFor.content?.map((item) => ({
              label: item.label,
              subtext: item.subtext,
            })) || [],
        },
      });
    }

    if (mode === "add") {
      if (isLimitReached) {
        setMode("list");
        return;
      }

      form.setFieldsValue({
        whatWeStandFor: {
          content: [{ label: "", subtext: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = whatWeStandFor?.content?.[editingIndex];

      if (item) {
        form.setFieldsValue({
          whatWeStandFor: {
            ...whatWeStandFor,
            content: [
              {
                label: item.label,
                subtext: item.subtext,
              },
            ],
          },
        });
      }
    }
  }, [mode, editingIndex, whatWeStandFor, form, isLimitReached, setMode]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Image"
              name={["whatWeStandFor", "image"]}
              rules={[{ required: true, message: "please upload image" }]}
            >
              <UploadImage listType="picture-card" />
            </Form.Item>

            <Form.Item
              label="Heading"
              name={["whatWeStandFor", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["whatWeStandFor", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden hidden sm:block">
          <table className="min-w-full">
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-3 px-4 text-left">Image</th>
                <th className="py-3 px-4 text-left">Heading</th>
                <th className="py-3 px-4 text-left">Subtext</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9F9F9]">
              {whatWeStandFor?.heading ||
              whatWeStandFor?.subtext ||
              whatWeStandFor?.image ? (
                <tr className="border-b">
                  <td className="px-4 py-3">
                    <Image
                      src={whatWeStandFor?.image}
                      width={80}
                      height={80}
                      className="object-contain"
                    />
                  </td>
                  <td className="px-4 py-3">{whatWeStandFor?.heading}</td>
                  <td className="px-4 py-3">{whatWeStandFor?.subtext}</td>
                  <td className="px-4 py-3 text-right">
                    <EditOutlined
                      className="text-primary text-xl cursor-pointer"
                      onClick={() => setIsEditing(true)}
                    />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    <Button type="dashed" onClick={() => setIsEditing(true)}>
                      Add Data
                    </Button>
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan={4} className="px-4 py-3 text-right">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    disabled={isLimitReached}
                    onClick={() => {
                      setMode("add");
                      setEditingIndex(null);
                    }}
                    className="text-primary font-semibold"
                  >
                    Add Sections
                  </Button>

                  {isLimitReached && (
                    <p className="text-sm text-red-500 mt-2">
                      Maximum {MAX_SECTIONS} sections allowed
                    </p>
                  )}
                </td>
              </tr>

              <tr>
                <td colSpan={4} className="p-4">
                  <p className="font-semibold mb-3">
                    Sections ({sectionCount}/{MAX_SECTIONS})
                  </p>

                  <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left">Title</th>
                        <th className="px-4 py-2 text-left">Description</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {whatWeStandFor?.content?.length ? (
                        whatWeStandFor.content.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-2">{item.label}</td>
                            <td className="px-4 py-2">{item.subtext}</td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-4 text-lg">
                                <EditOutlined
                                  className="cursor-pointer text-primary"
                                  onClick={() => {
                                    setMode("edit");
                                    setEditingIndex(index);
                                  }}
                                />
                                <Popconfirm
                                  title="Are you sure to delete this?"
                                  onConfirm={() =>
                                    onDelete(index, "whatWeStandFor")
                                  }
                                >
                                  <DeleteOutlined className="cursor-pointer text-red-500" />
                                </Popconfirm>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-6 text-center text-gray-500"
                          >
                            No sections added
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="block sm:hidden mb-4">
          <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
            <div>              
              <div><b className="text-primary">Image:</b></div>
              <Image src={whatWeStandFor?.image || "—"} alt="image" width={120}/>
            </div>

            <div>              
              <div><b className="text-primary">Heading:</b></div>
              <p>{whatWeStandFor?.heading || "—"}</p>
            </div>

            <div>              
              <div><b className="text-primary">Subtext:</b></div>
              <p>{whatWeStandFor?.subtext || "—"}</p>
            </div>

            <div>
              <EditOutlined
                onClick={() => setIsEditing(true)}
                className="cursor-pointer text-primary text-[18px]"
              />
            </div>
          </div>

          <Button
            type="primary"
            className="mt-3 text-primary font-semibold"
            disabled={isLimitReached}
            icon={<PlusOutlined />}
            onClick={() => {
              setMode("add");
              setEditingIndex(null);
            }}
          >
            Add Sections
          </Button>
          {isLimitReached && (
            <p className="text-sm text-red-500 mt-2">
              Maximum {MAX_SECTIONS} sections allowed
            </p>
          )}
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="sm:hidden mt-4 space-y-4">
          {whatWeStandFor?.content?.map((item: any, index: any) => (
            <div
              key={index}
              className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div>
                <div>
                  <p className="font-semibold text-primary">Label:</p> {item?.label}
                </div>
              </div>
              <div>
                <div>
                  <p className="font-semibold text-primary">Subtext:</p> {item?.subtext}
                </div>
              </div>

              <div className="flex justify-end gap-4 text-primary text-xl mt-3">
                <EditOutlined
                  className="cursor-pointer"
                  onClick={() => {
                    setMode("edit");
                    setEditingIndex(index);
                  }}
                />
                <Popconfirm
                  title="Are you sure to delete this?"
                  onConfirm={() => onDelete(index, "trackRecord")}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined className="cursor-pointer text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["whatWeStandFor", "content"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "label"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "subtext"]}
                        rules={[{ required: true, message: "Enter text" }]}
                      >
                        <TextArea rows={3} />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatWeStandFor;
